import { Box, Group, Text, useMantineTheme } from '@mantine/core';
import { ResponsiveBar } from '@nivo/bar';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import type {
  Granularity,
  RemindersAndCollections,
} from 'shared/hooks/use-load-reminders-and-collections';
import {
  useLocalizedCurrencyFormatter,
  useSafeLocalizedCompactCurrencyFormatter,
} from 'shared/utils/currency';

import { LegendSwatch } from './LegendSwatch';
import {
  BAR_PADDING,
  CHART_MARGIN,
  toBandColumn,
  toBucketLabel,
  toEffortCells,
  toMonthStartPeriods,
  toScaleIntensities,
  toShortMonth,
  toYearStartBuckets,
} from './reminders-and-collections-series';

const LABELS = i18nKeys.ANALYTICS.REMINDERS_AND_COLLECTIONS;

const AXIS = { tickPadding: 15, tickSize: 0 };

const FILL_PARENT = { width: '100%', height: '100%' } as const;

const COLLECTION_KEYS = ['collections'];

const EFFORT_STRIP_HEIGHT = 14;
const EFFORT_STRIP_GAP = 4;
const MONTH_LABEL_PADDING = EFFORT_STRIP_GAP + EFFORT_STRIP_HEIGHT + 12;

const YEAR_ROW_GAP = 15;
const SCALE_STEPS = 5;

const toColumnStyle = ({ left, width }: { left: number; width: number }) => ({
  left: `${left}%`,
  width: `${width}%`,
});

const LegendEntry = ({ color, label }: { color: string; label: string }) => (
  <Group gap="space3" component="li" style={{ listStyle: 'none' }}>
    <LegendSwatch color={color} />
    <Text size="xs">{label}</Text>
  </Group>
);

const LegendScale = ({
  label,
  fewer,
  more,
  swatch,
}: {
  label: string;
  fewer: string;
  more: string;
  swatch: string;
}) => (
  <Group gap="space3" component="li" style={{ listStyle: 'none' }}>
    <Text size="xs">{label}</Text>
    <Text size="xs" c="gray.6">
      {fewer}
    </Text>
    <Group gap={2} aria-hidden>
      {toScaleIntensities(SCALE_STEPS).map(intensity => (
        <LegendSwatch key={intensity} color={swatch} opacity={intensity} />
      ))}
    </Group>
    <Text size="xs" c="gray.6">
      {more}
    </Text>
  </Group>
);

type RemindersAndCollectionsChartProps = RemindersAndCollections & { granularity: Granularity };

export const RemindersAndCollectionsChart = ({
  periods,
  granularity,
}: RemindersAndCollectionsChartProps) => {
  const [hoveredPeriod, setHoveredPeriod] = useState<string | null>(null);
  const { t, currentLang } = useTranslation();
  const theme = useMantineTheme();
  const cashFormatter = useLocalizedCurrencyFormatter();
  const compactCashFormatter = useSafeLocalizedCompactCurrencyFormatter();
  const yearStarts = new Set(toYearStartBuckets(periods));
  const chartMargin =
    granularity === 'month'
      ? { ...CHART_MARGIN, bottom: CHART_MARGIN.bottom + YEAR_ROW_GAP }
      : CHART_MARGIN;
  const toMonthTick = (bucket: string) =>
    bucket === hoveredPeriod ? '' : toShortMonth(bucket, currentLang);

  const chartTheme = {
    grid: {
      line: {
        stroke: theme.colors.gray[3],
        strokeDasharray: 7,
        strokeDashoffset: 15,
      },
    },
    labels: {
      text: {
        fontSize: 14,
        fill: theme.colors.gray[6],
      },
    },
    text: {
      fontSize: 12,
      fill: theme.colors.gray[6],
    },
  };

  return (
    <Box style={{ ...FILL_PARENT, display: 'flex', flexDirection: 'column' }}>
      <Box style={{ flex: 1, position: 'relative' }}>
        <div style={FILL_PARENT} role="img" aria-label={t(LABELS.CASH_LABEL)}>
          <ResponsiveBar
            data={periods.map(({ period, collections }) => ({ period, collections }))}
            keys={COLLECTION_KEYS}
            indexBy="period"
            margin={chartMargin}
            padding={BAR_PADDING}
            axisLeft={{ ...AXIS, format: compactCashFormatter.format }}
            axisBottom={{
              ...AXIS,
              tickPadding: MONTH_LABEL_PADDING,
              tickValues: [...toMonthStartPeriods(periods)],
              ...(granularity === 'month'
                ? {
                    renderTick: ({ x, y, textX, textY, value }) => (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          x={textX}
                          y={textY}
                          textAnchor="middle"
                          dominantBaseline="text-before-edge"
                          style={chartTheme.text}
                        >
                          {toMonthTick(value)}
                        </text>
                        {yearStarts.has(value) && (
                          <text
                            x={textX}
                            y={textY + YEAR_ROW_GAP}
                            textAnchor="middle"
                            dominantBaseline="text-before-edge"
                            style={chartTheme.text}
                          >
                            {value.slice(0, 4)}
                          </text>
                        )}
                      </g>
                    ),
                  }
                : { format: toMonthTick }),
            }}
            enableGridX={false}
            enableLabel={false}
            colors={[theme.colors.success[4]]}
            tooltip={() => null}
            onMouseEnter={({ indexValue }) => setHoveredPeriod(String(indexValue))}
            onMouseLeave={() => setHoveredPeriod(null)}
            layers={[
              'grid',
              'axes',
              ({ bars, innerHeight, innerWidth }) => {
                const bandWidth = innerWidth / bars.length;

                return (
                  <g>
                    {bars.map(({ data }, column) => (
                      <rect
                        key={String(data.indexValue)}
                        x={column * bandWidth}
                        y={0}
                        width={bandWidth}
                        height={innerHeight}
                        fill="transparent"
                        onMouseEnter={() => setHoveredPeriod(String(data.indexValue))}
                        onMouseLeave={() => setHoveredPeriod(null)}
                      />
                    ))}
                  </g>
                );
              },
              'bars',
              ({ bars, innerHeight }) => {
                const hovered = bars.find(({ data }) => data.indexValue === hoveredPeriod);

                if (hovered === undefined) return null;

                const guideX = hovered.x + hovered.width / 2;

                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <line
                      x1={guideX}
                      y1={0}
                      x2={guideX}
                      y2={innerHeight}
                      stroke={theme.colors.gray[4]}
                    />
                    <text
                      x={guideX}
                      y={-30}
                      textAnchor="middle"
                      style={{
                        fontSize: 'var(--mantine-font-size-xs)',
                        fill: theme.colors.gray[6],
                      }}
                    >
                      {toBucketLabel(String(hovered.data.indexValue), currentLang, granularity)}
                    </text>
                    <text
                      x={guideX}
                      y={-10}
                      textAnchor="middle"
                      style={{
                        fontSize: 'var(--mantine-font-size-lg)',
                        fontWeight: 600,
                        fill: theme.colors.success[4],
                      }}
                    >
                      {`+${cashFormatter.format(Number(hovered.data.value))}`}
                    </text>
                  </g>
                );
              },
            ]}
            theme={chartTheme}
            barAriaLabel={({ data }) =>
              `${toBucketLabel(data.period, currentLang, granularity)}: ${cashFormatter.format(data.collections)}`
            }
          />
        </div>
        <Box
          role="group"
          aria-label={t(LABELS.REMINDERS_LABEL)}
          style={{
            position: 'absolute',
            left: chartMargin.left,
            right: chartMargin.right,
            bottom: chartMargin.bottom - EFFORT_STRIP_GAP - EFFORT_STRIP_HEIGHT,
            height: EFFORT_STRIP_HEIGHT,
          }}
        >
          {toEffortCells(periods).map(({ period, total, intensity }, column, cells) => (
            <Box
              key={period}
              style={{ position: 'absolute', ...toColumnStyle(toBandColumn(cells.length, column)) }}
              onMouseEnter={() => setHoveredPeriod(period)}
              onMouseLeave={() => setHoveredPeriod(null)}
            >
              <Box
                role="img"
                aria-label={`${toBucketLabel(period, currentLang, granularity)}: ${total}`}
                style={{
                  height: EFFORT_STRIP_HEIGHT,
                  borderRadius: 'var(--mantine-radius-xs)',
                  backgroundColor: theme.colors.purple[6],
                  opacity: intensity,
                }}
              />
              {period === hoveredPeriod && (
                <Text
                  fz="xs"
                  fw={600}
                  c="white"
                  style={{
                    backgroundColor: theme.colors.purple[6],
                    position: 'absolute',
                    top: `calc(${EFFORT_STRIP_HEIGHT}px + var(--mantine-spacing-space2))`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderRadius: 'var(--mantine-radius-sm)',
                    padding: '3px var(--mantine-spacing-space2)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {total}
                </Text>
              )}
            </Box>
          ))}
        </Box>
      </Box>
      <Group w="100%" gap="space10" justify="center" mt="space5" mb={0} component="ul" p={0}>
        <LegendScale
          label={t(LABELS.REMINDERS_LABEL)}
          fewer={t(LABELS.SCALE.FEWER)}
          more={t(LABELS.SCALE.MORE)}
          swatch={theme.colors.purple[6]}
        />
        <LegendEntry color={theme.colors.success[4]} label={t(LABELS.CASH_LABEL)} />
      </Group>
    </Box>
  );
};
